import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogData, ConfirmationDialogResult } from '@shared/models';
import { Observable } from 'rxjs';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';

@Injectable({
	providedIn: 'root'
})
export class ConfirmationDialogService {
	constructor(private dialog: MatDialog) { }

	/**
	 * Opens a confirmation dialog with custom configuration
	 * @param data Dialog configuration data
	 * @param config Optional MatDialog configuration
	 * @returns Observable that emits the user's choice
	 */
	open(data: ConfirmationDialogData, config?: MatDialogConfig): Observable<any> {
		const dialogConfig: MatDialogConfig = {
			width: '615px',
			maxWidth: '90vw',
			disableClose: true,
			autoFocus: true,
			restoreFocus: true,
			panelClass: 'confirmation-dialog-overlay',
			...config,
			data
		};

		const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open(
			ConfirmationDialogComponent,
			dialogConfig
		);

		return dialogRef.afterClosed();
	}

	/**
	 * Opens a simple confirmation dialog with Yes/No buttons
	 */
	confirm(title: string, message: string): Observable<boolean> {
		return this.open({
			title,
			message,
			buttons: [
				{
					label: 'No',
					color: '',
					class: 'btn-secondary',
					result: false
				},
				{
					label: 'Yes',
					color: 'primary',
					class: 'btn-primary',
					result: true
				}
			]
		});
	}

	/**
	 * Opens a delete confirmation dialog
	 */
	confirmDelete(title: string, message: string): Observable<boolean> {
		return this.open({
			title,
			message,
			icon: 'warning',
			iconColor: 'warn',
			buttons: [
				{
					label: 'Cancel',
					color: '',
					class: 'btn-secondary',
					result: false
				},
				{
					label: 'Delete',
					color: 'warn',
					class: 'btn-danger',
					result: true
				}
			]
		});
	}

	/**
	 * Opens a cancel trip confirmation dialog
	 * @param title Optional custom title (defaults to 'Cancel Trip?')
	 * @param message Optional custom message
	 */
	confirmCancelTrip(title?: string, message?: string, backLabelBtn?: string, confirmLabelBtn?: string, reasonTypeList?: any): Observable<ConfirmationDialogResult> {
		return this.open({
			title: title || 'Cancel Trip?',
			message: message || 'Are you sure you want to cancel this trip? This action cannot be undone and will notify both rider and driver.',
			icon: 'warning',
			iconColor: 'warn',
			buttons: [
				{
					label: backLabelBtn || 'Discard',
					color: '',
					class: 'btn-secondary',
					result: false
				},
				{
					label: confirmLabelBtn || 'Yes, Cancel',
					color: 'warn',
					class: 'btn-danger',
					result: true
				}
			],
			reasonTypeList
		});
	}

	/**
	 * Opens a complete trip confirmation dialog
	 * @param title Optional custom title (defaults to 'Complete Trip?')
	 * @param message Optional custom message
	 */
	confirmCompleteTrip(title?: string, message?: string, backLabelBtn?: string, confirmLabelBtn?: string, reasonTypeList?: any): Observable<ConfirmationDialogResult> {
		return this.open({
			title: title || 'Complete this Ride?',
			message: message || `You are confirming the completion of this ride. This action will finalize the fare and close the trip session.
				Please confirm all details are correct.`,
			icon: 'check_circle',
			iconColor: 'success',
			buttons: [
				{
					label: backLabelBtn || 'Cancel',
					color: '',
					class: 'btn-secondary',
					result: false
				},
				{
					label: confirmLabelBtn || 'Confirm Completion',
					color: 'primary',
					class: 'btn-primary',
					result: true
				}
			],
			reasonTypeList
		});
	}

	/**
	 * Opens a custom warning dialog
	 */
	warning(title: string, message: string, confirmLabel: string = 'Continue', cancelLabel: string = 'Cancel'): Observable<boolean> {
		return this.open({
			title,
			message,
			icon: 'warning',
			iconColor: 'warn',
			buttons: [
				{
					label: cancelLabel,
					color: '',
					class: 'btn-secondary',
					result: false
				},
				{
					label: confirmLabel,
					color: 'warn',
					class: 'btn-danger',
					result: true
				}
			]
		});
	}

	info(title: string, message: string, confirmLabel: string = 'Continue', cancelLabel: string = 'Cancel'): Observable<boolean> {
		return this.open({
			title,
			message,
			icon: 'info',
			iconColor: 'info',
			buttons: [
				{
					label: cancelLabel,
					color: '',
					class: 'btn-secondary',
					result: false
				},
				{
					label: confirmLabel,
					color: 'warn',
					class: 'btn-assign',
					result: true
				}
			]
		});
	}

	/**
	 * Opens a success confirmation dialog
	 */
	success(title: string, message: string, buttonLabel: string = 'OK'): Observable<boolean> {
		return this.open({
			title,
			message,
			icon: 'check_circle',
			iconColor: 'success',
			buttons: [
				{
					label: buttonLabel,
					color: 'primary',
					class: 'btn-success',
					result: true
				}
			]
		});
	}
}
